import {BPIconNames, Fragment, logJS, observer, pluralize, React, toJS, useLocalStore, utils} from '@api'
import {Icon} from '@blueprintjs/core'
import {Popover2} from '@api'
import {Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Switch, TextField} from '@material-ui/core'
import {BuilderCardLoader, GroupByToolbarChipComponent} from '@modules/catalog'
import {useVipLocalStore} from '@modules/VipLocalStore'
import {ICatalogUserSettings, IGroupBySettings, useWorkspacePresetDialogStore, WorkspacePresetDialogStoreContext} from '@stores'
import {WorkspacePresetDialogStore} from '@stores'
import {PrimaryIconButton} from 'centric-ui-components'
import * as css from './EditWorkspacePresetDialog.css'

export const EditWorkspacePresetDialog = observer(function EditWorkspacePresetDialog() {
  const store = useVipLocalStore((ws) => new WorkspacePresetDialogStore(ws)),
    {open, title, preset, onCancel, onOK, stores: {workspaces, currentUser}} = store

  if (!currentUser.info || !workspaces.workspace) return null

  return (
    <WorkspacePresetDialogStoreContext.Provider value={store}>
      <Dialog open={open} className={css.root} classes={{scrollPaper: css.dialogScrollPaper}} onClose={onCancel}>
        <DialogTitle className={css.dialogTitle}>
          {title}
          <MakePublicSwitch/>
        </DialogTitle>
        <DialogContent className={css.dialogContent}>
          <PresetFields/>
          {false && <PresetSummary/>}
        </DialogContent>
        <DialogActions className={css.dialogActions}>
          <Button onClick={onCancel} color='primary'>
            Cancel
          </Button>
          <Button onClick={onOK} color='secondary'>
            {preset?._id ? preset?.creator === currentUser?.info?.id ? 'Save' : 'Make a Copy' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </WorkspacePresetDialogStoreContext.Provider>
  )
})

/*
  creator?: string
  creationDate?: number
  copiedFromId?: string

  preset?: IWorkspacePreset
  workspaceId?: string

  label?: string
  slug?: string

  public?: boolean
 */

const PresetSummary = observer(function PresetSummary() {
  const store = useWorkspacePresetDialogStore()

  if (!store?.preset) return null

  const {preset: {preset}} = store

  if (!preset) return null

  return (<div className={css.summary}>
    <GroupByPresetSettings/>
    <CatalogPresetSettings/>
  </div>)
})


const CatalogPresetSettings = observer(function CatalogPresetSettings() {
    const {preset: {preset}, groupBy} = useWorkspacePresetDialogStore()

    if (!DEBUG || _.isEmpty(preset.catalog?.settings)) return null
    const {catalog: {settings}} = preset

    //logJS({preset})
    return (
      <div className={css.settingsGroup}>
        <div className={css.otherChips}>
          {_.keys(settings).filter(k => !_.includes(['vlp', 'groupBy'], k)).map((k: keyof ICatalogUserSettings, i) => {
            return (
              <Chip
                key={i}
                className={css.chip}
                label={
                  <div className={css.chipLabel}>
                    <label>{groupBy.labelForSetting(k as any)}:</label>
                    <SettingsChipValue k={k} value={preset.catalog.settings[k]} />
                  </div>}
                onDelete={() => {
                  delete preset.catalog.settings[k]
                }}
              />
            )
          })}
        </div>
      </div>)
  }
)

export const SettingsChipValue = observer<{k: keyof ICatalogUserSettings, value: any}>(function SettingsChipValue({k, value}) {
    const {stores: {catalog}} = useWorkspacePresetDialogStore()

  switch (k) {
    case 'focusedCatalogItemId':
      return <BuilderCardLoader className={css.catalogItemCard} id={value} esIndex={catalog.index} noFocus noHover />

    case 'cardView':
      return <CardViewFieldsPresetRenderer />

    default:
      return <div className={css.value}>{JSON.stringify(value)}</div>
  }
})

export const CardViewFieldsPresetRenderer = observer(function CardViewFieldsPresetRenderer() {
    const {preset: {preset: {catalog: {settings: {cardView}}}}} = useWorkspacePresetDialogStore()

  console.log(toJS(cardView))
    return (<div className={css.cardViewFields}>

    </div>)
})
const GroupByPresetSettings = observer(function GroupByPresetSettings() {
    const {preset: {preset}, catalog, groupBy} = useWorkspacePresetDialogStore()

    if (_.isEmpty(preset.catalog?.groupBy)) return null
    const {catalog: {groupBy: settings}} = preset

    //logJS({preset})
    return (
      <div className={css.settingsGroup}>
        <MerchHierarchyPresetSettings/>

        {settings.hierarchy && <div className={css.hierarchyChips}>
			<label>Group By Hierarchy:</label>
          {_.isEmpty(settings.hierarchy)
           ? <Chip className={css.chip} label={pluralize(catalog.catalogItemName)} onDelete={() => delete preset.catalog.groupBy.hierarchy}/>
           : <div className={css.chips}>
             {settings.hierarchy.map((group, i) => {
               const sfl = settings.settingsForLevel ? settings.settingsForLevel[i] : null

               return <Fragment key={i}>
                 <GroupByToolbarChipComponent
                   className={css.groupChip}
                   noCaret
                   group={groupBy.groupingByName.get(group)}
                   onDelete={() => settings.hierarchy = settings.hierarchy.filter(g => g != group)}/>

                 {(sfl?.subtitleFields ?? []).map((f, j) => (
                   <GroupByToolbarChipComponent
                     key={j}
                     className={css.groupChip}
                     noCaret
                     group={groupBy.groupingByName.get(f)}
                     onDelete={() => sfl.subtitleFields = sfl.subtitleFields.filter(field => field != f)}
                     icon={<div className={css.chipIcon}>S<sub>{j + 1}</sub></div>}
                   />))}
               </Fragment>
             })}
           </div>}
		</div>}

        <div className={css.otherChips}>
        {_.keys(settings).filter(k => !_.includes(['hierarchy', 'settingsForLevel'], k)).map((k: keyof IGroupBySettings, i) => {
          return (
            <Chip
              key={i}
              className={css.chip}
              label={
                <div className={css.chipLabel}>
                  <label>{groupBy.labelForSetting(k)}:</label>
                  <div className={css.value}>{JSON.stringify(preset.catalog.groupBy[k])}</div>
                </div>}
              onDelete={() => {
                delete preset.catalog.groupBy[k]
              }}
            />
          )
        })}
        </div>
      </div>)
  }
)

const MerchHierarchyPresetSettings = observer(function MerchHierarchyPresetSettings() {
  const {preset: {preset: {catalog}}, vlp} = useWorkspacePresetDialogStore()

  if (_.isEmpty(catalog?.settings?.vlp?.selected)) return null

  const {settings: {vlp: {selected}}} = catalog

  const {plans, folders, collections} = selected,
    {hierarchy: {itemOfType}} = vlp
  return (
    <div className={css.merchHierarchy}>
      {!_.isEmpty(plans) && (
        <div className={css.hierarchy} data-key='plans'>
          <label>Plans:</label>
          {plans.map(id => itemOfType('plan', id, null)).map((p, i) => <Chip className={css.item} key={i} label={p.name} onDelete={() => plans.splice(i, 1)} />)}
        </div>)}

      {!_.isEmpty(folders) && (
        <div className={css.hierarchy} data-key='folders'>
          <label>Folders:</label>
          {folders.map((hierarchy, i) => {
            const path = hierarchy.map(id => itemOfType('folder', id, null).name).join(' → ')
            return <Chip className={css.item} key={i} label={path} onDelete={() => folders.splice(i, 1)}/>
          })}
        </div>)}

      {!_.isEmpty(collections) && (
        <div className={css.hierarchy} data-key='collections'>
          <label>Collections:</label>
          {collections.map(id => itemOfType('collection', id, null)).map((c, i) => <Chip className={css.item} key={i} label={c.name} onDelete={() => collections.splice(i, 1)}/>)}
        </div>)}
    </div>)
})
import {Button as BPButton} from '@blueprintjs/core'

const PresetFields = observer(function PresetFields() {
    const {preset, readonly, onChangePresetLabel, onNamePresetKeyPress} = useWorkspacePresetDialogStore()

    return (
      <div className={css.fields}>
        <TextField
          autoFocus
          onFocus={e => e.target.select()}
          margin='dense'
          className={css.name}
          disabled={readonly}
          label='Preset Name'
          fullWidth
          value={preset?.label ?? 'Preset Name'}
          onChange={onChangePresetLabel}
          onKeyPress={onNamePresetKeyPress}
        />

        {false && <Popover2
			interactionKind='click'
			minimal
			content={
              <div className={css.icons}>
                {BPIconNames.map(icon => <PrimaryIconButton icon={icon} key={icon} onClick={() => preset.icon = icon}/>)}
              </div>}
		>
			<BPButton icon={<Icon icon={preset?.icon ?? 'blank'}/>} rightIcon='caret-down' text='Choose Icon'/>
		</Popover2>}
      </div>)
  }
)

const MakePublicSwitch = observer(function MakePublicSwitch() {
  const {preset, readonly, onTogglePublic} = useWorkspacePresetDialogStore()

  return (<FormControlLabel
    className={css.makePresetPublicSwitchLabel}
    control={<Switch checked={preset?.public === true} disabled={readonly} onChange={onTogglePublic} name='Shared'/>}
    label='Shared?'
  />)
})


// WEBPACK FOOTER //
// ./src/modules/workspaces/presets/EditWorkspacePresetDialog.tsx